"use client";

import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  getBookmeflightLink,
  getKnowroamingLink,
} from "@/actions/integration.api.action";
import { useToast } from "@/hooks/useToast";
import type { IntegrationLinkKey } from "@/utils/constants/services/serviceCatalog";

export type GetIntegrationLinkResult =
  { success: true; link: string } | { success: false; message: string };

const INTEGRATION_LABELS: Record<IntegrationLinkKey, string> = {
  bookmefly: "Flights",
  knowroaming: "eSim",
};

/** ******************************
 * Bookmeflight Link Mutation
 ****************************** */
const useBookmeflightLink = () => {
  return useMutation({
    mutationFn: getBookmeflightLink,
  });
};

/** ******************************
 * Knowroaming Link Mutation
 ****************************** */
const useKnowroamingLink = () => {
  return useMutation({
    mutationFn: getKnowroamingLink,
  });
};

/** ******************************
 * Integration Link Hook
 ****************************** */
const useIntegrationLink = () => {
  const bookmeflightMutation = useBookmeflightLink();
  const knowroamingMutation = useKnowroamingLink();

  /**
   * Get integration link
   * @param integrationKey Integration link key
   * @returns Integration link result
   */
  const getIntegrationLink = useCallback(
    async (
      integrationKey: IntegrationLinkKey,
    ): Promise<GetIntegrationLinkResult> => {
      try {
        const data =
          integrationKey === "bookmefly"
            ? await bookmeflightMutation.mutateAsync()
            : await knowroamingMutation.mutateAsync();

        if (!data?.link) {
          return {
            success: false,
            message: "No link was returned. Please try again.",
          };
        }

        return {
          success: true,
          link: data.link,
        };
      } catch (error) {
        return {
          success: false,
          message:
            error instanceof Error
              ? error.message
              : "Something went wrong. Please try again.",
        };
      }
    },
    [bookmeflightMutation, knowroamingMutation],
  );

  const isGettingIntegrationLink = useCallback(
    (integrationKey: IntegrationLinkKey) =>
      integrationKey === "bookmefly"
        ? bookmeflightMutation.isPending
        : knowroamingMutation.isPending,
    [bookmeflightMutation.isPending, knowroamingMutation.isPending],
  );

  return {
    getIntegrationLink,
    isGettingIntegrationLink,
  };
};

/** ******************************
 * Open Integration Link Hook
 ****************************** */
const useOpenIntegrationLink = () => {
  const { toast } = useToast();
  const { getIntegrationLink, isGettingIntegrationLink } = useIntegrationLink();

  const openIntegrationLink = useCallback(
    async (integrationKey: IntegrationLinkKey) => {
      const integrationLabel = INTEGRATION_LABELS[integrationKey];

      const result = await getIntegrationLink(integrationKey);

      if (!result.success) {
        toast({
          variant: "destructive",
          title: `Unable to open ${integrationLabel}`,
          description: result.message,
        });
        return result;
      }

      window.open(result.link, "_blank", "noopener,noreferrer");

      return result;
    },
    [getIntegrationLink, toast],
  );

  const isOpeningIntegrationLink = useCallback(
    (integrationKey: IntegrationLinkKey) =>
      isGettingIntegrationLink(integrationKey),
    [isGettingIntegrationLink],
  );

  return {
    openIntegrationLink,
    isOpeningIntegrationLink,
  };
};

export {
  useBookmeflightLink,
  useKnowroamingLink,
  useIntegrationLink,
  useOpenIntegrationLink,
};
